import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '../ConfirmDialog';
import { CrmStatusBadge } from './CrmStatusBadge';
import {
  getCrmStatus,
  initiateCrmAuth,
  disconnectCrm,
  type CrmConnectionInfo,
} from '../../lib/crmApi';
import { getApiErrorMessage } from '../../lib/apiErrors';

const PROVIDERS: Array<{
  id: string;
  label: string;
  description: string;
}> = [
  {
    id: 'salesforce',
    label: 'Salesforce',
    description: 'Connect your Salesforce org to generate buyer personas from real pipeline data.',
  },
  {
    id: 'hubspot',
    label: 'HubSpot',
    description: 'Connect HubSpot CRM to generate buyer personas from your deals and contacts.',
  },
  {
    id: 'pipedrive',
    label: 'Pipedrive',
    description: 'Connect Pipedrive to generate buyer personas from your sales pipeline.',
  },
];

export function CrmIntegrationSection() {
  const [connections, setConnections] = useState<CrmConnectionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [providerToDisconnect, setProviderToDisconnect] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await getCrmStatus();
      setConnections(data);
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setConnections([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus().catch(() => {});
  }, [loadStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('crm_connected');
    const crmError = params.get('crm_error');
    if (connected || crmError) {
      const url = new URL(window.location.href);
      url.searchParams.delete('crm_connected');
      url.searchParams.delete('crm_error');
      window.history.replaceState({}, '', url.toString());
      if (connected) {
        loadStatus().catch(() => {});
      }
      if (crmError) {
        setError(`CRM connection failed: ${crmError}`);
      }
    }
  }, [loadStatus]);

  const handleConnect = async (provider: string) => {
    setConnecting(provider);
    setError(null);
    try {
      const authUrl = await initiateCrmAuth(provider);
      window.location.href = authUrl;
    } catch (err) {
      setError(getApiErrorMessage(err));
      setConnecting(null);
    }
  };

  const confirmDisconnect = async () => {
    if (!providerToDisconnect) return;
    const provider = providerToDisconnect;
    setProviderToDisconnect(null);
    setDisconnecting(provider);
    setError(null);
    try {
      await disconnectCrm(provider);
      await loadStatus();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setDisconnecting(null);
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading integrations...</div>;

  return (
    <section className="bg-white p-6 rounded-lg border">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">CRM Integrations</h2>
      <p className="text-sm text-gray-600 mb-4">
        Connect your CRM to generate AI buyer personas from real pipeline data.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {PROVIDERS.map((prov) => {
          const conn = connections.find((c) => c.provider === prov.id);
          return (
            <div
              key={prov.id}
              className="flex items-start justify-between p-4 border rounded-lg gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-900">{prov.label}</h3>
                  {conn && <CrmStatusBadge status={conn.status} />}
                </div>
                <p className="text-sm text-gray-600">{prov.description}</p>
                {conn?.lastSyncAt && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last synced: {new Date(conn.lastSyncAt).toLocaleString()}
                  </p>
                )}
                {conn?.lastSyncError && (
                  <p className="text-xs text-red-600 mt-1">{conn.lastSyncError}</p>
                )}
              </div>
              <div className="shrink-0">
                {conn ? (
                  <button
                    type="button"
                    onClick={() => setProviderToDisconnect(prov.id)}
                    disabled={disconnecting === prov.id}
                    className="px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    {disconnecting === prov.id ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleConnect(prov.id)}
                    disabled={connecting === prov.id}
                    className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {connecting === prov.id ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={providerToDisconnect !== null}
        title={
          providerToDisconnect
            ? `Disconnect ${PROVIDERS.find((p) => p.id === providerToDisconnect)?.label ?? providerToDisconnect}?`
            : 'Disconnect CRM?'
        }
        description="CRM-generated personas will remain, but synced data will be deleted."
        confirmLabel="Disconnect"
        variant="destructive"
        loading={disconnecting !== null}
        onConfirm={() => void confirmDisconnect()}
        onCancel={() => setProviderToDisconnect(null)}
      />
    </section>
  );
}
